import { FC, useEffect, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Col, Dropdown, Menu, Row } from 'antd';
import { AxiosResponse } from 'axios';
import { TbBraces, TbDownload, TbFileTypeCsv, TbFileTypeXls, TbFileZip } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { openNotionFormModal } from '@/shared/components/modals/GlobalModals/NotionFormModal/store';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { dayjs } from '@/utilities';

import DropdownOptionContent from '../DropdownOptionContent';

// interface ExportDataDropdownProps {}

type ExportOption = 'zip' | 'xlsx' | 'json' | 'csv';

const exportOptions: Record<
    ExportOption,
    {
        description: string;
        extension: string;
        icon: JSX.Element;
        label: string;
    }
> = {
    zip: {
        description: 'Complete archive with JSON, CSV, manifest, and redaction notes.',
        extension: 'zip',
        icon: <TbFileZip size='1rem' />,
        label: 'Portable ZIP',
    },
    xlsx: {
        description: 'Workbook with one sheet per export dataset.',
        extension: 'xlsx',
        icon: <TbFileTypeXls size='1rem' />,
        label: 'Excel Workbook',
    },
    json: {
        description: 'Structured archive for programmatic portability.',
        extension: 'json',
        icon: <TbBraces size='1rem' />,
        label: 'JSON Archive',
    },
    csv: {
        description: 'ZIP archive of normalized CSV files for spreadsheet and warehouse import.',
        extension: 'zip',
        icon: <TbFileTypeCsv size='1rem' />,
        label: 'CSV Archive',
    },
};

const getDownloadFilename = (res: AxiosResponse, exportOption: ExportOption) => {
    const disposition = res?.headers?.['content-disposition'] ?? '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);

    if (filenameMatch?.[1]) {
        return decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
    }

    return `Countercyclical-Data-Export-${dayjs().format('YYYY-MM-DD')}.${exportOptions[exportOption].extension}`;
};

const ExportDataDropdown: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    const handleDataExport = async (exportOption: ExportOption) => {
        const option = exportOptions[exportOption];
        const toastId = toast.loading(`Exporting ${option.label}`, {
            description: `${dayjs(new Date()).format('LLL')}`,
            icon: <LoadingOutlined spin />,
        });

        try {
            setLoading(true);

            const res = await authorizedAxiosInstance.post(
                `/api/export/user/${exportOption}`,
                {},
                { responseType: 'blob' },
            );
            const contentType = res.headers?.['content-type'];
            const url = window.URL.createObjectURL(
                new Blob([res.data], { type: typeof contentType === 'string' ? contentType : undefined }),
            );
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', getDownloadFilename(res, exportOption));
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`${option.label} Download Started`, {
                id: toastId,
                description: 'Your export should appear in your downloads shortly.',
            });
        } catch (error) {
            captureException(error);

            toast.error('Export Failed', {
                id: toastId,
                description: 'Please contact support if the issue persists.',
                action: {
                    label: 'Contact Support',
                    onClick: () => openNotionFormModal('support'),
                },
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dropdown
            trigger={['click']}
            placement='bottomLeft'
            popupRender={() => (
                <DropdownMenuPanel
                    onClick={({ key }) => {
                        handleDataExport(key as ExportOption);
                    }}
                    style={{ width: 16 * 18 }}
                >
                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem', verticalAlign: '0.1rem' }}>
                        Export Data
                    </small>
                    <Menu.Divider />
                    {(Object.entries(exportOptions) as Array<[ExportOption, (typeof exportOptions)[ExportOption]]>).map(
                        ([key, option]) => (
                            <Menu.Item key={key}>
                                <DropdownOptionContent
                                    avatarStyle={{ background: 'transparent', border: 'none' }}
                                    description={option.description}
                                    icon={option.icon}
                                    title={
                                        <>
                                            {option.label}
                                            <small style={{ marginLeft: '0.5rem', color: token.colorTextTertiary }}>
                                                (.{option.extension})
                                            </small>
                                        </>
                                    }
                                />
                            </Menu.Item>
                        ),
                    )}
                </DropdownMenuPanel>
            )}
        >
            <DropdownButton loading={loading} style={{ verticalAlign: '-1.125rem' }}>
                <Row>
                    <Col flex='auto' style={{ textAlign: 'left' }}>
                        <TbDownload
                            size={'1rem'}
                            style={{
                                verticalAlign: '-0.1rem',
                                marginRight: '0.375rem',
                                marginLeft: '0.5rem',
                            }}
                        />
                        Export...
                    </Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
};

export default ExportDataDropdown;
